import React, { useState, useEffect } from 'react';
import ConfirmButton from '@common/components/confirm-button';
import { Container } from '@common/components/container';
import { TopNavigationBar } from '@common/components/navigation-bar';
import { useHistory, useRouteMatch } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import Card from '@common/components/card';
import { CardContent } from '@common/components/card/card-content';
import { TextInputHookForm } from '@common/components/form/inputs/text';
import Icon from '@common/components/icon';
import { ColorInputHookForm } from '@common/components/form/inputs/color/color';
import { HookFormWrapper } from '@common/hooks/use-hook-form';
import { createRatingScale } from '../../action/create-rating-scale';
import { AlertService } from '@common/services/alert';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { RatingScalePayload } from '@modules/performance/types';
import { useRequest } from '@common/services/api/useRequest';
import Spinner from '@common/components/spinner';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import RatingScaleExample from '../../components/rating-scale-example';
import RatingScalesModal, {
  RatingScale,
} from '../../components/rating-scales-modal/rating-scales-modal';
import { TextareaInputHookForm } from '@common/components/form/inputs/textarea/textarea';

const RatingItem = ({
  control,
  t,
  item,
  index,
  itemIndex,
  onDelete,
}: {
  control: any;
  t: any;
  item: any;
  index: number;
  itemIndex: number;
  onDelete: (ratingScaleId: string) => Promise<string | void>;
}) => {
  return (
    <div className="tw-flex tw-flex-row tw-items-start tw-gap-4 tw-bg-gray-custom-50 tw-rounded-lg tw-p-4">
      <div className="tw-text-base tw-h-[80px]  tw-font-medium tw-items-center tw-justify-center">
        <div className="tw-items-center tw-mt-2">{index}</div>
      </div>
      <div className="tw-flex tw-flex-row tw-gap-2 tw-w-full">
        <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
            <div
              className="tw-rounded-[6px] tw-px-3 tw-py-[10px] tw-border
            tw-border-solid tw-border-gray-border !tw-shadow-input"
            >
              <ColorInputHookForm
                name={`scalePoints.${itemIndex}.color`}
                hideValue
                className="!tw-rounded-[4px] !tw-border-none !tw-w-[16px] !tw-h-[16px]"
                colorPickerClassName="!tw-top-[30px]"
                colorPickerInnerClassName="!tw-w-[250px]"
                colorInputPreviewClassName="!tw-border-r-[0px]"
                control={control}
                defaultColor="#CCCCCC"
              />
            </div>
            <TextInputHookForm
              name={`scalePoints.${itemIndex}.label`}
              control={control}
              className="!tw-h-5 !tw-shadow-input"
              placeholder={t('performance:rating_scale_name_placeholder')}
            />
          </div>
          <TextareaInputHookForm
            name={`scalePoints.${itemIndex}.description`}
            control={control}
            className="!tw-shadow-input"
            rows={3}
            placeholder={t('performance:rating_scale_description_placeholder')}
          />
        </div>
        <Button
          size="large"
          type="inverted"
          icon="close"
          className="!tw-rounded-lg !tw-px-[10px] !tw-py-3 !tw-h-[36px]
          !tw-flex !tw-items-center !tw-justify-center !tw-cursor-pointer !tw-shadow-input
          !tw-border !tw-border-solid !tw-border-gray-border !tw-hover:tw-bg-red-50
          !tw-hover:tw-border-red-300"
          onClick={async () => {
            await onDelete(item.id);
          }}
        />
      </div>
    </div>
  );
};

type FormData = {
  scalePoints: Array<{
    id: string;
    label: string;
    color: string;
    description: string;
  }>;
};

const ManageRatings = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const match = useRouteMatch();
  const organisation = useAppSelector(selected);
  const { networkId } = match.params as { networkId: string };
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const [initialValues, setInitialValues] = useState<any>(null);
  // const languages = useAppSelector((state) => state.language.available);
  // const defaultLanguage = useAppSelector(
  //   (state) => state.loggedUser.user.language_locale
  // );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRatingScale, setSelectedRatingScale] =
    useState<{
      points: number;
      scales: RatingScale[];
    } | null>(null);
  const [deletedRatingScaleIds, setDeletedRatingScaleIds] = useState<string[]>(
    []
  );
  const [formFunctions, setFormFunctions] =
    useState<{
      setValue: any;
      getValues: any;
    } | null>(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);


  // const [languagePredicates, setLanguagePredicates] = useState([
  //   {
  //     filter: EPredicateFilters[EPredicateFields.COMPETENCY_LANGUAGE],
  //     value: {
  //       attribute: EPredicateFields.COMPETENCY_LANGUAGE,
  //       comparison: 'eq' as const,
  //       value: defaultLanguage,
  //     },
  //     options: languages || [],
  //   },
  // ]);

  // const [language] = useMemo(() => {
  //   return [
  //     languagePredicates.find(
  //       (p) => p.filter === EPredicateFilters[EPredicateFields.COMPETENCY_LANGUAGE]
  //     )?.value.value,
  //   ];
  // }, [languagePredicates]);

  const apiConfig = {
    // language: language || '',
    transform: (res: any) => {
      return {
        ...res,
        data: res.data.map((item: any) => ({
          ...item,
        })),
      };
    },
  };

  const { data: ratingScales, loading } = useRequest(
    `/v1/organisations/${organisation.id}/performance/rating-scales`,
    apiConfig
  );

  useEffect(() => {
    if (ratingScales && !initialValues) {
      setInitialValues(JSON.parse(JSON.stringify(ratingScales)));
    }
  }, [ratingScales, initialValues]);

  // useEffect(() => {
  //   if (language && refetch) {
  //     refetch();
  //   }
  // }, [language]);

  const handleLocalDelete = (
    ratingScaleId: string,
    setValue: any,
    getValues: any
  ) => {
    const currentScalePoints = getValues('scalePoints');
    const updatedScalePoints = currentScalePoints.filter(
      (scalePoint: any) => scalePoint.id !== ratingScaleId
    );
    setValue('scalePoints', updatedScalePoints);
    setHasLocalChanges(true);

    // If it's an existing rating scale (not a new one), track it for deletion
    if (!ratingScaleId.startsWith('new_')) {
      setDeletedRatingScaleIds((prev) => [...prev, ratingScaleId]);
    }
  };

  const handleConfirmRatingScale = () => {
    if (!selectedRatingScale || !formFunctions) return;

    const { setValue, getValues } = formFunctions;

    // Add rating scales to local form state
    const currentScalePoints = getValues('scalePoints') || [];
    const newRatingScales = selectedRatingScale.scales.map((scale) => ({
      id: `new_${Date.now()}_${Math.random().toString(36)}_${scale.id}`,
      label: scale.label,
      description: scale.description || '',
      color: scale.color,
    }));

    setValue('scalePoints', [...currentScalePoints, ...newRatingScales]);
    setHasLocalChanges(true);

    setShowConfirmModal(false);
    setSelectedRatingScale(null);
    AlertService.success(t('performance:rating_scales_template_added'));
  };

  const hasChanges = (current: any, initial: any): boolean => {
    if (!initial || current.id?.startsWith('new_')) return false;

    return (
      (current.label || '') !== (initial.label || '') ||
      (current.description || '') !== (initial.description || '') ||
      (current.color || '') !== (initial.color || '')
    );
  };

  useEffect(() => {
    if (ratingScales) {
      setInitialValues(JSON.parse(JSON.stringify(ratingScales)));
      setHasLocalChanges(false);
    }
  }, [ratingScales]);

  const onSubmit = async (formData: any): Promise<void> => {
    if (!organisation?.id) {
      AlertService.error(t('common:organisation_not_found'));
      return;
    }

    try {
      const currentScalePoints = formData.scalePoints || [];

      const remainingScalePoints = currentScalePoints.filter(
        (currentItem: any) => {
          return !deletedRatingScaleIds.includes(String(currentItem.id));
        }
      );

      // Check if there are any changes
      const hasDeleted = deletedRatingScaleIds.length > 0;
      const hasNewItems = remainingScalePoints.some(
        (item: any) => item.id && item.id.toString().startsWith('new_')
      );
      const hasUpdatedItems = remainingScalePoints.some((currentItem: any) => {
        if (currentItem.id && currentItem.id.toString().startsWith('new_')) {
          return false; // New items are not updates
        }
        const initialItem = initialValues?.find(
          (initial: any) => String(initial.id) === String(currentItem.id)
        );
        return hasChanges(currentItem, initialItem);
      });

      if (
        hasDeleted ||
        hasNewItems ||
        hasUpdatedItems ||
        remainingScalePoints.length !== (initialValues?.length || 0)
      ) {
        const payload: RatingScalePayload[] = remainingScalePoints.map(
          (scalePoint: any) => {
            const payloadItem: RatingScalePayload = {
              // locale: language,
              label: scalePoint.label,
              description: scalePoint.description,
              color: scalePoint.color,
            };

            // if (scalePoint.id && !scalePoint.id.toString().startsWith('new_')) {
            //   payloadItem.id = scalePoint.id;
            // }

            return payloadItem;
          }
        );

        const result = await createRatingScale(organisation.id, payload);

        setDeletedRatingScaleIds([]);
        setHasLocalChanges(false);

        if (formFunctions && result?.data) {
          const responseData = Array.isArray(result.data)
            ? result.data
            : [result.data];

          const responseMap = new Map();
          responseData.forEach((item: any) => {
            const key = `${item.label}_${item.color}`;
            responseMap.set(key, item);
          });

          const updatedScalePoints = remainingScalePoints.map(
            (scalePoint: any) => {
              if (
                scalePoint.id &&
                scalePoint.id.toString().startsWith('new_')
              ) {
                const key = `${scalePoint.label}_${scalePoint.color}`;
                const createdScale = responseMap.get(key);
                if (createdScale) {
                  return {
                    ...scalePoint,
                    id: createdScale.id,
                  };
                }
              }
              return scalePoint;
            }
          );

          formFunctions.setValue('scalePoints', updatedScalePoints);
          setInitialValues(JSON.parse(JSON.stringify(updatedScalePoints)));
        }

        AlertService.success(
          t('performance:rating_scales_updated_successfully')
        );
        setShowSaveModal(false);
      } else {
        setHasLocalChanges(false);
        setShowSaveModal(false);
      }
    } catch (error: any) {
      AlertService.forStatus(error.status_code, {
        warning: t('performance:rating_scales_create_warning'),
        error: t('performance:rating_scales_create_error'),
      });
    }
  };

  return (
    <Container name="ManageRatings">
      <HookFormWrapper<FormData>
        initialValues={{
          scalePoints: ratingScales,
        }}
        onSubmit={onSubmit}
        mode="onBlur"
      >
        {({
          control,
          handleSubmit,
          formState: { isSubmitting, isDirty },
          watch,
          setValue,
          getValues,
        }) => {
          // Set form functions for modal access
          if (!formFunctions) {
            setFormFunctions({ setValue, getValues });
          }

          const watchedScalePoints = watch('scalePoints');

          const addNewRating = () => {
            const currentScalePoints = getValues('scalePoints');
            const newId = `new_${Date.now()}_${Math.random().toString(36)}`;
            const newRating = {
              id: newId,
              label: '',
              color: '#CCCCCC',
              description: '',
            };
            setValue('scalePoints', [...currentScalePoints, newRating]);
            setHasLocalChanges(true);
          };

          const handleDelete = async (ratingScaleId: string) => {
            handleLocalDelete(ratingScaleId, setValue, getValues);
            return ratingScaleId;
          };

          const handleRatingScaleSelect = (points: number) => {
            const getColorScheme = (point: number) => {
              const baseColors = [
                'tw-bg-red-dark',
                'tw-bg-orange-dark',
                'tw-bg-yellow-accent',
                'tw-bg-green-dark',
                'tw-bg-blue-brand',
              ];
              return baseColors.slice(0, point);
            };

            const getHexColor = (tailwindClass: string): string => {
              const colorMap: { [key: string]: string } = {
                'tw-bg-red-dark': '#DC2626',
                'tw-bg-orange-dark': '#EA580C',
                'tw-bg-yellow-accent': '#D97706',
                'tw-bg-green-dark': '#16A34A',
                'tw-bg-blue-brand': '#2563EB',
              };
              return colorMap[tailwindClass] || '#CCCCCC';
            };

            const predefinedScales: {
              [key: number]: Array<{
                label: string;
                color: string;
                description: string | null;
              }>;
            } = {
              5: [
                {
                  label: t('performance:unsatisfactory'),
                  color: getHexColor(getColorScheme(5)[0]),
                  description: null,
                },
                {
                  label: t('performance:below_expectations'),
                  color: getHexColor(getColorScheme(5)[1]),
                  description: null,
                },
                {
                  label: t('performance:meets_expectations'),
                  color: getHexColor(getColorScheme(5)[2]),
                  description: null,
                },
                {
                  label: t('performance:above_expectations'),
                  color: getHexColor(getColorScheme(5)[3]),
                  description: null,
                },
                {
                  label: t('performance:excellent_far_exceeds_expectations'),
                  color: getHexColor(getColorScheme(5)[4]),
                  description: null,
                },
              ],
              4: [
                {
                  label: t('performance:unacceptable'),
                  color: getHexColor(getColorScheme(4)[0]),
                  description: null,
                },
                {
                  label: t('performance:needs_improvement'),
                  color: getHexColor(getColorScheme(4)[1]),
                  description: null,
                },
                {
                  label: t('performance:meets_expectations'),
                  color: getHexColor(getColorScheme(4)[2]),
                  description: null,
                },
                {
                  label: t('performance:exceeds_expectations'),
                  color: getHexColor(getColorScheme(4)[3]),
                  description: null,
                },
              ],
              3: [
                {
                  label: t('performance:needs_improvement'),
                  color: getHexColor(getColorScheme(3)[0]),
                  description: null,
                },
                {
                  label: t('performance:meets_expectations'),
                  color: getHexColor(getColorScheme(3)[1]),
                  description: null,
                },
                {
                  label: t('performance:exceeds_expectations'),
                  color: getHexColor(getColorScheme(3)[2]),
                  description: null,
                },
              ],
            };

            const selectedScale = predefinedScales[points];
            if (selectedScale) {
              setSelectedRatingScale({
                points,
                scales: selectedScale.map((scale, index) => ({
                  id: `predefined_${index + 1}`,
                  label: scale.label,
                  description: scale.description || undefined,
                  index: index + 1,
                  color: scale.color,
                })),
              });
              setShowConfirmModal(true);
            }
          };

          const EmptyRatingScales = () => {
            return (
              <CardContent className="!tw-p-5 !tw-gap-5 !tw-flex !tw-flex-col">
                <div className="tw-text-lg tw-text-gray-textDark tw-font-medium">
                  {t('performance:no_rating_scales_added_yet')}
                </div>
                <RatingScaleExample
                  points={5}
                  descriptionKey="performance:simple_familiar_and_offers_a_clear_middle_point"
                  t={t}
                  onClick={handleRatingScaleSelect}
                />
                <RatingScaleExample
                  points={4}
                  descriptionKey="performance:eliminates_a_neutral_option_to_push_managers_to_make_a_judgement_call"
                  t={t}
                  onClick={handleRatingScaleSelect}
                />
                <RatingScaleExample
                  points={3}
                  descriptionKey="performance:reduces_subjectivity_and_forces_clearer_distinctions"
                  t={t}
                  onClick={handleRatingScaleSelect}
                />
                <div className="tw-flex tw-items-center">
                  <div className="tw-h-[1px] tw-bg-gray-border tw-flex-grow" />
                  <span className="tw-mx-4 tw-text-gray-400 tw-font-medium">
                    {t('performance:or')}
                  </span>
                  <div className="tw-h-[1px] tw-bg-gray-border tw-flex-grow" />
                </div>
                <Button
                  type="inverted"
                  size="large"
                  className="!tw-text-left !tw-p-5 hover:!tw-shadow-md"
                  onClick={addNewRating}
                >
                  {t('performance:create_custom_rating_scale')}
                </Button>
              </CardContent>
            );
          };

          return (
            <>
              <TopNavigationBar
                breadcrumbs={[
                  { name: t('performance:tab_performance') },
                  {
                    name: t('performance:competency_library'),
                    path: `${basePath}/performance/competency-library/all`,
                  },
                  { name: t('performance:manage_rating_scale') },
                ]}
                title={t('performance:manage_rating_scale')}
                action={
                  <>
                    {/* <FiltersToolbar
                      predicates={languagePredicates}
                      setPredicates={setLanguagePredicates}
                      organisation={organisation}
                      searchEnabled={false}
                      className="!tw-w-auto"
                    /> */}
                    {isDirty ||
                    deletedRatingScaleIds.length > 0 ||
                    hasLocalChanges ? (
                      <ConfirmButton
                        title={t('learning:gen_ai_unsaved_changes_title')}
                        description={t(
                          'learning:gen_ai_unsaved_changes_description'
                        )}
                        onCancel={() => {}}
                        onConfirm={() => history.push(
                            `${basePath}/performance/competency-library/all`
                          )}
                        confirmText={t(
                          'learning:gen_ai_unsaved_changes_confirm'
                        )}
                        cancelText={t('learning:gen_ai_unsaved_changes_cancel')}
                        danger
                      >
                        <Button size="large">{t('performance:go_back')}</Button>
                      </ConfirmButton>
                    ) : (
                      <Button
                        size="large"
                        onClick={() => history.push(
                            `${basePath}/performance/competency-library/all`
                          )}
                      >
                        {t('performance:go_back')}
                      </Button>
                    )}
                    <Button
                      onClick={() => setShowSaveModal(true)}
                      type="primary"
                      isLoading={isSubmitting}
                      disabled={
                        isSubmitting ||
                        (!isDirty &&
                          ratingScales.length === 0 &&
                          !hasLocalChanges) ||
                        (watchedScalePoints && watchedScalePoints.length === 0)
                      }
                    >
                      {t('common:save')}
                    </Button>
                  </>
                }
              />
              {showSaveModal && (
                <ModalWindow
                  show
                  onHide={() => setShowSaveModal(false)}
                  size="small"
                  className="EditableModule"
                >
                  <ModalContent
                    title={t('performance:save_changes')}
                    onHide={() => setShowSaveModal(false)}
                    onConfirm={handleSubmit(onSubmit)}
                    confirmButtonText={t(
                      'performance:confirm_and_save_changes'
                    )}
                    confirmButtonLoading={isSubmitting}
                  >
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <div className="tw-text-base tw-text-gray-text tw-font-normal">
                        {t('performance:save_changes_description')}
                      </div>
                    </div>
                  </ModalContent>
                </ModalWindow>
              )}
              <Container.Content className="tw-gap-6 !tw-w-[650px]">
                <Card containerClassName="tw-w-full !tw-p-0 !tw-rounded-lg">
                  {loading ? (
                    <Spinner centered />
                  ) : watchedScalePoints && watchedScalePoints.length > 0 ? (
                    <CardContent className="!tw-w-[90%] !tw-p-5 !tw-gap-5 !tw-flex !tw-flex-col">
                      {watchedScalePoints?.map((item, itemIndex) => {
                        return (
                          <RatingItem
                            key={item.id}
                            control={control}
                            t={t}
                            index={itemIndex + 1}
                            item={item}
                            itemIndex={itemIndex}
                            onDelete={handleDelete}
                          />
                        );
                      })}
                      {watchedScalePoints.length > 0 && (
                        <div
                          className="tw-flex tw-flex-row tw-items-center tw-gap-[2px] tw-cursor-pointer
                        hover:tw-bg-blue-50 tw-rounded-lg tw-p-2 tw-transition-colors"
                          onClick={addNewRating}
                        >
                          <Icon
                            type="add"
                            size={16}
                            className="tw-text-blue-brand"
                          />
                          <span className="tw-text-base tw-font-medium tw-text-blue-brand">
                            {t('performance:add_rating')}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  ) : (
                    <EmptyRatingScales />
                  )}
                </Card>
              </Container.Content>
            </>
          );
        }}
      </HookFormWrapper>

      {/* Confirmation Modal */}
      <RatingScalesModal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setSelectedRatingScale(null);
        }}
        scales={selectedRatingScale?.scales || []}
        title={t('performance:confirm_rating_scale')}
        showConfirmButton
        onConfirm={handleConfirmRatingScale}
        confirmButtonText={t('performance:confirm')}
        isLoading={false}
      />
    </Container>
  );
};

export default ManageRatings;
